export interface HelpArticle {
  id: string
  slug: string
  title: string
  seoTitle: string
  metaDescription: string
  keywords: string[]
  category: string
  categorySlug: string
  content: string
  relatedArticles: string[]
}

export interface HelpCategory {
  name: string
  slug: string
  icon: string
  description: string
}

export const helpCategories: HelpCategory[] = [
  {
    name: 'Getting Started',
    slug: 'getting-started',
    icon: '🚀',
    description: 'Install the app, create your account, and get set up',
  },
  {
    name: 'Flings',
    slug: 'flings',
    icon: '💫',
    description:
      'Everything about creating and managing your 7-day connections',
  },
  {
    name: 'Settings',
    slug: 'settings',
    icon: '⚙️',
    description: 'Customize your profile, notifications, and preferences',
  },
  {
    name: 'Plus & Billing',
    slug: 'plus-billing',
    icon: '✨',
    description: 'Upgrade to Plus, manage subscriptions, and payments',
  },
  {
    name: 'Safety & Support',
    slug: 'safety-support',
    icon: '🛡️',
    description: 'Block users, report issues, and stay safe',
  },
  {
    name: 'Troubleshooting',
    slug: 'troubleshooting',
    icon: '🔧',
    description: 'Fix common issues and get answers fast',
  },
]

export const helpArticles: HelpArticle[] = [
  // GETTING STARTED
  {
    id: 'install',
    slug: 'install-flinglink',
    title: 'Installing FlingLink',
    seoTitle: 'Install FlingLink PWA on iPhone & Android | Quick Setup',
    metaDescription:
      'Install FlingLink as a Progressive Web App on your phone. Add FlingLink to your home screen on iOS Safari and Android Chrome in seconds.',
    keywords: [
      'install FlingLink',
      'FlingLink app download',
      'PWA install',
      'add to home screen',
    ],
    category: 'Getting Started',
    categorySlug: 'getting-started',
    content: `
## How to Install FlingLink on Your Phone

FlingLink is a Progressive Web App (PWA) — basically, you can install it straight to your home screen without hitting up an app store. No waiting, no storage drama. Let's get you set up.

### Installing on iPhone (iOS Safari)

1. Open **Safari** and go to [me.flinglink.app](https://me.flinglink.app)
2. Tap the **Share button** (that square with an arrow) at the bottom
3. Scroll down and tap **"Add to Home Screen"**
4. Tap **"Add"** in the top right corner
5. Done! FlingLink now lives on your home screen like any other app ✨

### Installing on Android (Chrome)

1. Open **Chrome** and go to [me.flinglink.app](https://me.flinglink.app)
2. Tap the **three dots menu** (⋮) in the top right
3. Select **"Add to Home screen"** or **"Install app"**
4. Tap **"Add"** or **"Install"**
5. You'll find FlingLink on your home screen and in your app drawer 🎉

### Installing on Desktop (Chrome/Edge)

1. Go to [me.flinglink.app](https://me.flinglink.app) in Chrome or Edge
2. Look for the **install icon** (➕) in the address bar
3. Click **"Install FlingLink"**
4. The app opens in its own window — no browser tabs needed

### Why Install FlingLink?

- **Quick access** from your home screen (no more typing URLs)
- **Push notifications** so you never miss a Spark or message
- **Offline support** for basic features when signal's sketchy
- **Full-screen experience** without browser chrome getting in the way
- **Auto-updates** — always running the latest version

### Troubleshooting

**"Add to Home Screen" option missing?**
- Make sure you're using Safari on iOS or Chrome on Android
- Try logging into the app first
- Refresh the page and try again

**App not appearing on home screen?**
- Check your app drawer (Android)
- Search for "FlingLink" on your device
- Reinstall if needed — your data is safe in the cloud

### Next Steps

Once you're installed:
1. [Create your account](/help/signup)
2. [Set up your profile](/help/profile-settings)
3. [Create your first Fling](/help/create-fling)
    `,
    relatedArticles: ['signup', 'profile-settings', 'create-fling'],
  },
  {
    id: 'signup',
    slug: 'signup',
    title: 'Creating Your Account',
    seoTitle: 'How to Sign Up for FlingLink | Create Account Fast',
    metaDescription:
      'Create your FlingLink account in minutes. Signup with email, magic link, or social login via Google, TikTok, Discord, Snapchat, and more.',
    keywords: [
      'FlingLink signup',
      'create FlingLink account',
      'FlingLink registration',
      'sign up',
    ],
    category: 'Getting Started',
    categorySlug: 'getting-started',
    content: `
## How to Sign Up for FlingLink

Creating a FlingLink account takes like 2 minutes. Pick whatever signup method vibes with you.

### Signup Methods

**1. Email & Password (Classic)**
- Enter your name, email, and phone number
- Create a secure password
- Verify your phone via SMS code
- You're in!

**2. Magic Link (No Password Needed)**
- Enter your email address
- Check your inbox for a magic link
- Click it and you're logged in instantly
- No password to remember, no password to forget 🧠

**3. Social Login (One-Tap)**
Connect instantly with:
- Google
- TikTok
- Discord
- Snapchat
- Twitch

### Step-by-Step Signup

**Step 1: Choose Your Method**

Visit [me.flinglink.app](https://me.flinglink.app) and tap "Get Started" or "Sign Up"

**Step 2: Enter Your Details**
- **Name:** Your real name or what you want to be called
- **Email:** For login and notifications
- **Phone:** Required for account security (we keep it private)

**Step 3: Verify Your Phone**
- Enter the 6-digit code sent via SMS
- Codes expire after 10 minutes
- Tap "Resend" if you don't get it

**Step 4: Set a Password** (if using email signup)
- Minimum 8 characters
- Mix of letters and numbers recommended
- This is your key to logging in later

**Step 5: Complete Onboarding**
- Create your avatar (or upload a photo)
- Add your socials (optional but recommended)
- Set your notification preferences

### Why We Need Your Phone Number

Your phone number is used for:
- **Account security** — verify it's really you
- **Important notifications** — like when someone accepts your Fling
- **Two-factor authentication** — keeping your account locked down

We never share your number with other users unless you choose to on Day 7.

### Already Have an Account?

Head to [login](/help/login) to sign back in.
    `,
    relatedArticles: ['login', 'profile-settings', 'install'],
  },
  {
    id: 'login',
    slug: 'login',
    title: 'Logging In',
    seoTitle: 'FlingLink Login | Password, Magic Link & Social Sign In',
    metaDescription:
      'Log in to FlingLink with password, magic link, or social accounts. Quick access to your Flings, chats, and connections.',
    keywords: [
      'FlingLink login',
      'sign in FlingLink',
      'FlingLink password',
      'magic link login',
    ],
    category: 'Getting Started',
    categorySlug: 'getting-started',
    content: `
## How to Log In to FlingLink

Multiple ways to get back into your account — pick your favorite.

### Login Options

**Email + Password**
1. Enter your email address
2. Enter your password
3. Tap "Log In"
4. Welcome back! 👋

**Magic Link (Passwordless)**
1. Tap "Use Magic Link" on the login screen
2. Enter your email
3. Check your inbox for the magic link
4. Click it — you're in!

**Social Login**
1. Tap your preferred platform (Google, TikTok, Discord, Snapchat, Twitch)
2. Authorize FlingLink
3. Automatically logged in

### Forgot Your Password?

No stress, it happens to everyone:

1. Tap **"Forgot password?"** on the login screen
2. Enter your email address
3. Check your inbox for a reset link
4. Click the link and create a new password
5. Log in with your fresh password

### Magic Link Not Working?

- Check your **spam/junk folder** — sometimes it lands there
- Links expire after **15 minutes** — request a new one if needed
- Make sure you entered the **correct email**
- Try a different email client if issues persist

### Social Login Issues?

- Check your **internet connection**
- Make sure you've **granted OAuth permissions**
- Try a **different browser**
- Clear cookies and try again
- [Contact support](/help/report) if it keeps failing

### Stay Logged In

FlingLink keeps you logged in on your device. If you're getting logged out frequently:
- Make sure cookies are enabled
- Don't use incognito/private mode
- Check that your browser isn't clearing data automatically
    `,
    relatedArticles: ['signup', 'login-issues', 'install'],
  },

  // FLINGS
  {
    id: 'what-is-fling',
    slug: 'what-is-fling',
    title: 'What is a Fling?',
    seoTitle: 'What is a Fling on FlingLink? | 7-Day Connection Guide',
    metaDescription:
      'Learn what a Fling is on FlingLink. A 7-day structured connection with daily Sparks, private chat, Pulse checks, and shareable Recaps.',
    keywords: [
      'what is a Fling',
      'FlingLink Fling',
      '7-day connection',
      'Fling meaning',
    ],
    category: 'Flings',
    categorySlug: 'flings',
    content: `
## What is a Fling?

A Fling is FlingLink's core experience — a **7-day structured connection** between two people. Think of it as a low-pressure way to see if you vibe with someone, without the endless "what are we?" conversations.

### The Fling Experience

Every Fling includes:

**🔥 Daily Sparks**
Creative prompts that drop each day. Answer yours to unlock theirs. Keeps the convo fresh without the "hey wyd" energy.

**💬 Private Chat**
End-to-end encrypted messaging between you and your Fling partner. Say what you want, it stays between you two.

**💓 Pulse Check**
Rate your interest daily (More / Same / Less). Track how the connection evolves over 7 days — no awkward talks needed.

**📸 Recap Card**
At the end, get a beautiful shareable card summarizing your journey. Post it, share it, or keep it private.

### How a Fling Works

**Day 1:** Accept the invite, set the vibe, start connecting
**Days 2-6:** Daily Sparks, chat, Pulse checks
**Day 7:** The moment of truth — share numbers or friendly landing

### Setting Up a Fling

When you create a Fling, you set:

- **The Vibe:** Playful, Chill, Curious, Cozy, or Spicy
- **Boundaries:** Pick 2 (No overthinking, Slow pace, Keep it light, etc.)
- **The Goal:** What you're hoping for (See if we click, Have one great date, etc.)

Your match sees these before accepting, so everyone's on the same page from day one.

### Why 7 Days?

Seven days is the sweet spot:
- Long enough to actually get to know someone
- Short enough to stay exciting
- Clear endpoint means no ghosting or endless limbo
- Forces a decision — no more "what are we?"

### What Happens After Day 7?

Three options:
1. **Share numbers** — both consent, exchange contacts, continue outside FlingLink
2. **Friendly landing** — end gracefully with a kind message
3. **Let it expire** — no action needed, Fling closes naturally

Either way, you get a Recap card to remember the journey.
    `,
    relatedArticles: [
      'create-fling',
      'daily-sparks',
      'pulse-check',
      'fling-recaps',
    ],
  },
  {
    id: 'create-fling',
    slug: 'create-fling',
    title: 'Creating a Fling',
    seoTitle: 'How to Create a Fling on FlingLink | Send Invite Link',
    metaDescription:
      'Create a Fling on FlingLink and send an invite link. Set your vibe, boundaries, and goal, then share with someone special.',
    keywords: [
      'create Fling',
      'FlingLink invite',
      'start a Fling',
      'send Fling link',
    ],
    category: 'Flings',
    categorySlug: 'flings',
    content: `
## How to Create a Fling

Ready to start a 7-day connection? Here's how to create and send a Fling invite.

### Step-by-Step Guide

**Step 1: Tap the Create Button**
Hit the **"+"** button or **"Create Fling"** from your dashboard.

**Step 2: Set the Vibe**
Choose the energy you're going for:
- 😏 **Playful** — Fun, flirty, not too serious
- 😌 **Chill** — Relaxed, no pressure
- 🤔 **Curious** — Getting to know each other
- 🥰 **Cozy** — Warm, comfortable vibes
- 🌶️ **Spicy** — Turning up the heat

**Step 3: Pick Your Boundaries**
Select 2 boundaries to set expectations:
- No overthinking 🧠🚫
- No expectations
- Slow pace
- Keep it light
- Stay private 🤫
- Text daily 📱

**Step 4: Choose Your Goal**
What are you hoping for?
- See if we click
- Have one great date
- Make a new friend
- Just vibes, no labels

**Step 5: Generate Your Link**
Tap **"Create Link"** and you'll get a unique invite URL like:
\`flingl.ink/u/yourname\`

**Step 6: Share It**
Send your link via:
- Instagram DM
- Snapchat
- WhatsApp
- Text message
- Any messaging app

### Optional: Add a PIN

Want extra security? Add a 4-digit PIN:
1. Toggle on **"PIN Protection"**
2. Set a 4-digit code
3. Share the PIN separately with your match
4. They'll need both the link AND the PIN to accept

### Tips for Sharing

- **Be direct:** "Hey, wanna try this 7-day thing? Low pressure."
- **Add context:** Explain what FlingLink is if they haven't heard of it
- **Keep it casual:** It's meant to be fun, not formal
- **Share the PIN separately:** If you're using one, send it in a different message

### What Happens Next?

Once they click your link:
1. They'll see your vibe, boundaries, and goal
2. They can accept as-is or propose changes
3. When they accept, your Fling begins!
4. You'll get a notification when they join

### Managing Pending Invites

Your invite stays active for **7 days**. You can:
- Copy the link again to reshare
- Cancel the invite if you change your mind
- Edit details before they accept
    `,
    relatedArticles: [
      'accept-fling',
      'what-is-fling',
      'daily-sparks',
      'manage-flings',
    ],
  },
  {
    id: 'accept-fling',
    slug: 'accept-fling',
    title: 'Accepting a Fling Invite',
    seoTitle: 'How to Accept a Fling Invite | Join & Start Connecting',
    metaDescription:
      'Accept a FlingLink invite and start your 7-day connection. Review the vibe, boundaries, and goal before joining.',
    keywords: [
      'accept Fling',
      'join FlingLink',
      'Fling invite',
      'accept invite',
    ],
    category: 'Flings',
    categorySlug: 'flings',
    content: `
## How to Accept a Fling Invite

Someone sent you a FlingLink? Here's how to join and start your 7-day connection.

### Step-by-Step

**Step 1: Click the Link**
Tap the invite link they sent you. It'll look something like:
\`flingl.ink/u/theirname\`

**Step 2: Sign Up or Log In**
- New to FlingLink? Create an account (takes 2 mins)
- Already have an account? Log in

**Step 3: Enter PIN (if required)**
If they added PIN protection, enter the 4-digit code they shared with you.

**Step 4: Review the Fling**
You'll see:
- **The Vibe** they set (Playful, Chill, etc.)
- **Boundaries** they chose
- **Their Goal** for the Fling

**Step 5: Accept or Propose Changes**
Two options:
- **Accept** — Start the Fling with their settings
- **Propose Changes** — Suggest different vibe/boundaries (they'll need to approve)

**Step 6: You're In!**
Once accepted, your 7-day Fling begins immediately. Your first Spark will be waiting!

### What to Expect

- **Day 1:** First Spark drops, start chatting
- **Daily:** New Spark each day, Pulse checks
- **Day 7:** Decision time — share numbers or friendly landing

### Can't Find the Link?

- Check your messages/DMs where they sent it
- Ask them to resend if it's been more than 7 days (links expire)
- Make sure you're clicking the full URL

### PIN Not Working?

- Double-check the code they gave you
- Watch for extra spaces when copying
- Ask them to confirm the correct PIN
- They can send a new invite if needed
    `,
    relatedArticles: ['what-is-fling', 'daily-sparks', 'signup'],
  },
  {
    id: 'daily-sparks',
    slug: 'daily-sparks',
    title: 'Daily Sparks',
    seoTitle: 'FlingLink Daily Sparks | Answer Prompts & Earn Streaks',
    metaDescription:
      "Learn about FlingLink Daily Sparks. Answer creative prompts, send voice notes, unlock your partner's response, and build your streak.",
    keywords: [
      'Daily Sparks',
      'FlingLink prompts',
      'Spark questions',
      'daily prompts',
    ],
    category: 'Flings',
    categorySlug: 'flings',
    content: `
## Daily Sparks

Sparks are the heart of FlingLink — creative prompts that drop each day to keep your connection fresh and interesting.

### What Are Sparks?

Every day of your 7-day Fling, you'll get a new Spark. These are fun questions or prompts designed to:
- Break the ice without the awkward small talk
- Learn something real about each other
- Keep the conversation flowing naturally
- Replace the dreaded "hey wyd" loop

### How Sparks Work

**1. New Spark Drops**
A fresh prompt appears at the start of each day.

**2. Answer It**
Respond with:
- **Text:** Type out your answer
- **Voice Note:** Record up to 30 seconds of audio (way more personal)

**3. Unlock Their Response**
Once you answer, you can see what they said. It's a mutual reveal — no peeking until you've shared too.

**4. Chat Opens**
After both of you respond, chat unlocks for the day. Discuss your answers, go deeper, or just vibe.

### Example Sparks

- "What's a movie you've seen more than 10 times?"
- "Describe your perfect Sunday morning"
- "What's something you're weirdly competitive about?"
- "Send a voice note of your best impression"
- "What's the last thing that made you laugh out loud?"

### Streaks 🔥

Complete both Sparks (yours and viewing theirs) to maintain your streak:
- **Day 1:** 🔥 1
- **Day 7:** 🔥 7 (perfect streak!)

Miss a day? You've got a **Grace Save** — one free pass because life happens.

### Tips for Great Spark Responses

- **Be honest** — Authentic answers spark better conversations
- **Try voice notes** — They're more personal and memorable
- **Don't overthink it** — First instinct is usually best
- **Have fun** — It's not a job interview

### Premium Spark Decks

Plus members and purchasers get access to themed Spark packs:
- **Deep Talk:** Meaningful questions that go beyond surface level
- **Spicy:** Turn up the heat 🌶️
- **Future Planning:** Where is this going?
- **Nostalgia:** Childhood memories and throwbacks
    `,
    relatedArticles: [
      'what-is-fling',
      'pulse-check',
      'fling-chat',
      'flinglink-plus',
    ],
  },
  {
    id: 'pulse-check',
    slug: 'pulse-check',
    title: 'Pulse Checks',
    seoTitle: 'FlingLink Pulse Check | Rate Your Connection Daily',
    metaDescription:
      'Use FlingLink Pulse Check to rate your connection daily. Track interest levels with More, Same, or Less — no awkward conversations needed.',
    keywords: [
      'Pulse Check',
      'FlingLink rating',
      'daily check-in',
      'interest level',
    ],
    category: 'Flings',
    categorySlug: 'flings',
    content: `
## Pulse Checks

Pulse is your daily vibe check — a simple way to track how your connection is evolving without any awkward "so where is this going?" talks.

### How Pulse Works

After responding to your daily Spark, you'll rate your Pulse:

- **📈 More** (Green) — Interest is growing, feeling good about this
- **😌 Same** (Yellow) — Steady vibes, still figuring it out
- **📉 Less** (Red) — Interest is fading, might not be the one

### Why Pulse Matters

**For You:**
- Track your own feelings over 7 days
- Notice patterns in how your interest changes
- Make Day 7 decisions easier

**For Your Partner:**
- Optional sharing — you choose if they see your Pulse
- Mutual transparency builds trust
- No guessing games about where you stand

### Sharing Your Pulse

You control visibility:
- **Share:** Your partner sees your daily Pulse
- **Keep Private:** Only you see it

If you both share, you can see each other's Pulse journey throughout the Fling.

### Reading the Signals

**Both showing "More"?**
Great sign! You're both feeling it.

**Mixed signals?**
Normal! Connection takes time. Keep going.

**Both showing "Less"?**
Maybe this isn't the one — and that's okay. Day 7 gives you a clean exit.

### Tips

- **Be honest** — Pulse is for you as much as them
- **Don't stress** — One "Less" day doesn't mean it's over
- **Check the trend** — Overall direction matters more than single days
- **Use it for reflection** — Great tool for understanding your own feelings
    `,
    relatedArticles: ['daily-sparks', 'what-is-fling', 'number-sharing'],
  },
  {
    id: 'fling-chat',
    slug: 'fling-chat',
    title: 'Chat in a Fling',
    seoTitle: 'FlingLink Chat | Private Messaging During Your Fling',
    metaDescription:
      'Chat privately during your FlingLink Fling. End-to-end encrypted messaging, voice notes, and photo sharing for Plus members.',
    keywords: [
      'FlingLink chat',
      'Fling messaging',
      'private chat',
      'encrypted messages',
    ],
    category: 'Flings',
    categorySlug: 'flings',
    content: `
## Chat in a Fling

Once you've both responded to the daily Spark, chat opens up. Here's everything you need to know about messaging in FlingLink.

### Chat Features

**End-to-End Encryption**
Your messages are private — only you and your Fling partner can read them. Not even we can see what you're saying.

**Text Messages**
Standard messaging, but make it cute. Say what you want to say.

**Voice Messages**
Record and send audio messages. Way more personal than text, and you can hear their actual voice.

**Photo Sharing** (Plus)
Plus members can share photos directly in chat. Keep it classy.

### When Chat Unlocks

Chat becomes available after **both** of you complete the daily Spark. This ensures:
- You're both engaged
- There's always something to talk about
- No one-sided conversations

### Chat Availability

- **During Fling:** Full access to messaging
- **After Day 7:** Chat closes when Fling ends
- **After Number Share:** Continue via text/phone instead

### Chat Tips

- **Respond to Sparks first** — Unlocks chat for the day
- **Use voice notes** — More personal, shows effort
- **Keep it flowing** — Don't let convos die on read
- **Be yourself** — Authenticity > trying too hard

### Chat vs. Your Socials

Remember, you can also chat on your socials (Insta, WhatsApp, Snap) if you prefer. FlingLink chat is just one option — use whatever feels natural.
    `,
    relatedArticles: ['daily-sparks', 'what-is-fling', 'number-sharing'],
  },
  {
    id: 'number-sharing',
    slug: 'number-sharing',
    title: 'Number Sharing (Day 7)',
    seoTitle: 'FlingLink Number Share | Exchange Phone Numbers on Day 7',
    metaDescription:
      'On Day 7, decide to share phone numbers with your FlingLink match. Both must consent to exchange contacts and continue outside the app.',
    keywords: [
      'number sharing',
      'Day 7',
      'exchange numbers',
      'FlingLink decision',
    ],
    category: 'Flings',
    categorySlug: 'flings',
    content: `
## Number Sharing on Day 7

Day 7 is decision time. After a week of Sparks, chats, and Pulse checks, you both choose what happens next.

### The Day 7 Decision

Three paths forward:

**1. Share Numbers 📱**
Both of you consent to exchange phone numbers. You get their number, they get yours, and you can continue the connection outside FlingLink.

**2. Friendly Landing 🤝**
End things gracefully. Send a kind message, get your Recap, and part ways with dignity. No ghosting, no drama.

**3. Let It Expire ⏰**
Take no action and the Fling closes naturally. Sometimes that's the answer too.

### How Number Sharing Works

1. **Both must consent** — It only happens if you BOTH choose to share
2. **Simultaneous reveal** — Numbers are shown to both at the same time
3. **It's permanent** — Once shared, you can't undo it
4. **Continue outside FlingLink** — Text, call, FaceTime — whatever you want

### What If Only One Person Wants to Share?

If you choose "Share" but they choose "Friendly Landing" (or vice versa), numbers aren't exchanged. The Fling ends with a friendly landing instead.

This protects both people — no one gets your number without your explicit consent.

### After Sharing Numbers

- FlingLink chat closes
- You've got their actual phone number now
- Continue the connection via text/call
- Start a new Fling later if you want to do it again

### Tips for Day 7

- **Trust your gut** — You've had 7 days to figure out how you feel
- **No pressure** — Friendly landing is a valid choice
- **Be honest** — If you're not feeling it, that's okay
- **Celebrate the journey** — Either way, you get a Recap to remember it
    `,
    relatedArticles: ['what-is-fling', 'pulse-check', 'fling-recaps'],
  },
  {
    id: 'fling-recaps',
    slug: 'fling-recaps',
    title: 'Fling Recaps',
    seoTitle: 'FlingLink Recap Cards | Share Your 7-Day Journey',
    metaDescription:
      'Get a beautiful Recap card after your FlingLink Fling. Customize themes, share on socials, or keep it private.',
    keywords: [
      'Fling Recap',
      'Recap card',
      'FlingLink summary',
      'shareable card',
    ],
    category: 'Flings',
    categorySlug: 'flings',
    content: `
## Fling Recaps

Every Fling ends with a Recap — a beautiful, shareable card that captures your 7-day journey together.

### What's in a Recap?

Your Recap includes:
- **Days completed** together
- **Streak count** 🔥
- **Vibe** you set at the start
- **Boundaries** you agreed on
- **Pulse journey** visualization
- **Outcome** (Graduated, Renewed, Friendly Landing)

### Creating Your Recap

Recaps are **automatically generated** when your Fling ends. Here's how to access and customize:

1. Go to your **Flings** tab
2. Find the completed Fling
3. Tap **"View Recap"**
4. Choose a **theme** (more themes with Plus)
5. Preview and **share**

### Sharing Options

**Generate a Public Link**
Create a shareable URL anyone can view. Perfect for:
- Posting on your story
- Sharing with friends
- Soft launching 👀

**Download Image**
Save the Recap as an image to share anywhere.

**Keep It Private**
Don't want to share? That's cool too. Your Recap stays in your history.

### Soft Launch Mode 🤫

Want to share but keep it lowkey? Toggle **Soft Launch Mode** to:
- Blur your partner's name and photo
- Show "Me & ???" instead of names
- Keep the mystery alive

### Plus Recap Features

Plus members get:
- **All Recap themes** (exclusive designs)
- **Higher quality images**
- **Custom branding options**
- **Priority theme access** for new releases

### Recap Themes

Choose from multiple designs:
- Classic (clean and minimal)
- Gradient (colorful vibes)
- Dark Mode (sleek and moody)
- Seasonal themes (limited editions)
- Plus-exclusive designs
    `,
    relatedArticles: ['what-is-fling', 'number-sharing', 'flinglink-plus'],
  },
  {
    id: 'manage-flings',
    slug: 'manage-flings',
    title: 'Managing Your Flings',
    seoTitle: 'FlingLink Dashboard | View Active, Pending & Past Flings',
    metaDescription:
      'Manage all your FlingLinks from one dashboard. View active Flings, pending invites, and past connections with Recaps.',
    keywords: [
      'manage Flings',
      'FlingLink dashboard',
      'Fling list',
      'active Flings',
    ],
    category: 'Flings',
    categorySlug: 'flings',
    content: `
## Managing Your Flings

Your Flings dashboard is command central. Here's how to keep track of everything.

### Fling Categories

**Active Flings**
Currently running 7-day connections. This is where the action is.

**Pending Invites**
Invites you've sent that are waiting for acceptance. Links stay active for 7 days.

**Past Flings**
Completed connections with their Recaps. Your dating history, beautifully organized.

### What You Can Do

**For Active Flings:**
- View current day and progress
- Access chat and Sparks
- Check Pulse history
- See time remaining

**For Pending Invites:**
- Copy link to reshare
- Cancel the invite
- Edit details (before accepted)
- Check when it expires

**For Past Flings:**
- View and share Recaps
- See the full journey
- Remember the good times

### Free vs Plus Limits

**Free Account:**
- 1 active Fling at a time
- Unlimited pending invites
- Full history access

**Plus Account:**
- Up to 10 active Flings simultaneously
- Priority matching
- All premium features

### Tips for Managing Multiple Flings (Plus)

- **Stay organized** — Each Fling has its own chat and Sparks
- **Set reminders** — Don't forget to respond to Sparks
- **Be present** — Quality over quantity
- **Track your Pulses** — Know where each connection stands
    `,
    relatedArticles: ['create-fling', 'what-is-fling', 'flinglink-plus'],
  },

  // SETTINGS
  {
    id: 'profile-settings',
    slug: 'profile-settings',
    title: 'Profile Settings',
    seoTitle: 'Edit Your FlingLink Profile | Name, Avatar & Account',
    metaDescription:
      'Update your FlingLink profile settings. Change your name, upload an avatar, manage account info, and customize your presence.',
    keywords: [
      'FlingLink profile',
      'edit profile',
      'avatar',
      'account settings',
    ],
    category: 'Settings',
    categorySlug: 'settings',
    content: `
## Profile Settings

Make your FlingLink profile yours. Here's how to customize everything.

### Accessing Profile Settings

1. Tap the **menu** or **profile icon**
2. Go to **Settings**
3. Select **Profile**

### What You Can Edit

**Display Name**
The name shown to your Fling partners. Use your real name or whatever you want to be called.

**Avatar**
Your profile picture. Options:
- **Upload photo** — From your camera roll
- **Take new photo** — Use your camera
- **AI Avatar** — Generate one (Plus feature)
- **Remove** — Go avatar-free

**Email Address**
Used for login and notifications. Changing it requires verification.

**Phone Number**
Required for account security. Can be updated with SMS verification.

### Avatar Tips

- **Show your face** — Builds trust and connection
- **Good lighting** — Natural light works best
- **Be yourself** — Authenticity wins
- **AI avatars** — Fun alternative if you're camera shy (Plus)

### Account Information

View your:
- Account creation date
- Subscription status (Free or Plus)
- Plus expiry date (if applicable)
- Total Flings completed
    `,
    relatedArticles: [
      'social-profiles',
      'notification-settings',
      'flinglink-plus',
    ],
  },
  {
    id: 'social-profiles',
    slug: 'social-profiles',
    title: 'Social Profiles',
    seoTitle: 'Add Social Media to FlingLink | Instagram, Snapchat & More',
    metaDescription:
      'Connect your social media to FlingLink. Add Instagram, Snapchat, TikTok, X, YouTube, Twitch, and Discord to your profile.',
    keywords: [
      'social profiles',
      'add Instagram',
      'connect socials',
      'FlingLink socials',
    ],
    category: 'Settings',
    categorySlug: 'settings',
    content: `
## Social Profiles

Connect your socials to let your Fling partners find you across platforms.

### Supported Platforms

- **Instagram** — @yourusername
- **Snapchat** — yourusername
- **TikTok** — @yourusername
- **X (Twitter)** — @yourusername
- **YouTube** — @yourchannel
- **Twitch** — yourusername
- **Discord** — yourusername#0000

### How to Add Socials

1. Go to **Settings → Social Profiles**
2. Tap the platform you want to add
3. Enter your username (without the @ for most)
4. Tap **Save**

### Why Add Socials?

- **More ways to connect** — Partners can find you elsewhere
- **Show your personality** — Your content says a lot about you
- **Build trust** — Real profiles = real person
- **Continue the convo** — Easy to move to DMs

### Visibility

Your social links are shown on your profile within Flings. Partners can tap to view your profiles directly.

### Tips

- **Keep them updated** — Old usernames don't help
- **Only add active accounts** — Quality over quantity
- **Consider privacy** — Only add what you're comfortable sharing
- **It's optional** — You don't have to add any
    `,
    relatedArticles: ['profile-settings', 'privacy-settings'],
  },
  {
    id: 'notification-settings',
    slug: 'notification-settings',
    title: 'Notification Preferences',
    seoTitle: 'FlingLink Notifications | Push, Email & Alert Settings',
    metaDescription:
      'Customize FlingLink notifications. Control push alerts, email notifications, and what updates you receive.',
    keywords: [
      'FlingLink notifications',
      'push notifications',
      'email alerts',
      'notification settings',
    ],
    category: 'Settings',
    categorySlug: 'settings',
    content: `
## Notification Preferences

Control what notifications you get and how you get them.

### Notification Types

**New Spark Available**
Daily prompt is ready for you to answer.

**Partner Responded**
Your Fling partner answered their Spark — time to see what they said.

**New Message**
Someone sent you a chat message.

**Invite Accepted**
Someone joined your Fling! Time to start connecting.

**Fling Ending Soon**
Day 7 is approaching — decision time coming up.

**Marketing & Updates**
News, features, and promotions from FlingLink.

### Push Notifications

**To enable push notifications:**

1. Make sure notifications are allowed in your device settings
2. Grant browser notification permission when prompted
3. Toggle on the notifications you want in FlingLink settings

**iOS:** Settings → Safari → Notifications → Allow

**Android:** Settings → Apps → Chrome → Notifications → Allow

### Email Notifications

Backup notifications sent to your email:
- Important account updates
- Security alerts
- Optional marketing emails

### Customizing

Go to **Settings → Notifications** to toggle each type on or off. Find your perfect balance between staying informed and not being overwhelmed.

### Not Getting Notifications?

Check out our [troubleshooting guide](/help/notification-issues) for fixes.
    `,
    relatedArticles: ['notification-issues', 'profile-settings'],
  },
  {
    id: 'privacy-settings',
    slug: 'privacy-settings',
    title: 'Privacy Settings',
    seoTitle: 'FlingLink Privacy | Block, Report & Safety Controls',
    metaDescription:
      'Manage your FlingLink privacy settings. Block users, report issues, and control your safety and visibility.',
    keywords: ['FlingLink privacy', 'block users', 'report', 'safety settings'],
    category: 'Settings',
    categorySlug: 'settings',
    content: `
## Privacy Settings

Your safety and privacy matter. Here's how to control your FlingLink experience.

### Safety Features

**Block Users**
Prevent specific people from contacting you or starting Flings with you.

**Report Issues**
Flag inappropriate behavior, content, or bugs for our team to review.

**Privacy Controls**
Manage what information is visible and to whom.

### How to Block Someone

1. Go to **Settings → Privacy**
2. Tap **"Block User"**
3. Enter their username
4. Confirm the block

**What happens when you block:**
- All communication stops immediately
- They can't start new Flings with you
- Existing Flings are affected
- They're NOT notified that you blocked them

### When to Block

- Unwanted messages or contact
- Inappropriate behavior
- You simply don't want to connect with them
- Any safety concerns

### When to Report

- Harassment or abuse
- Inappropriate content
- Spam or scam attempts
- Terms of Service violations
- Bugs or technical issues

Reports are reviewed within 24-48 hours. We take safety seriously.

### Your Data

FlingLink respects your privacy:
- End-to-end encrypted chats
- No selling your data
- Delete your account anytime
- GDPR compliant

See our [Privacy Policy](/privacy) for full details.
    `,
    relatedArticles: ['block-users', 'report', 'delete-account'],
  },
  {
    id: 'appearance-settings',
    slug: 'appearance-settings',
    title: 'Appearance Settings',
    seoTitle: 'FlingLink Dark Mode | Theme & Display Preferences',
    metaDescription:
      'Customize FlingLink appearance. Switch between light mode, dark mode, or match your system settings.',
    keywords: ['dark mode', 'FlingLink theme', 'appearance', 'light mode'],
    category: 'Settings',
    categorySlug: 'settings',
    content: `
## Appearance Settings

Make FlingLink look the way you want.

### Theme Options

**Light Mode ☀️**
Classic bright theme. Clean, crisp, easy to read.

**Dark Mode 🌙**
Easier on the eyes, especially at night. Saves battery on OLED screens.

**System Default 📱**
Automatically matches your device's theme setting. Light during the day, dark at night (if your phone does that).

### How to Change Theme

1. Go to **Settings**
2. Tap **"Appearance"**
3. Select your preferred theme
4. Changes apply immediately

### Why Dark Mode?

- **Easier on eyes** — Less strain in low light
- **Battery saver** — Uses less power on OLED screens
- **Looks cool** — Let's be honest
- **Better for late-night Sparks** — No blinding yourself at 2am

### Tips

- **Match your phone** — System default keeps everything consistent
- **Try both** — See what feels better for you
- **Night owls** — Dark mode is your friend
    `,
    relatedArticles: ['profile-settings', 'notification-settings'],
  },

  // PLUS & BILLING
  {
    id: 'flinglink-plus',
    slug: 'flinglink-plus',
    title: 'FlingLink Plus Membership',
    seoTitle: 'FlingLink Plus Features | Upgrade Benefits & Premium',
    metaDescription:
      'Discover FlingLink Plus benefits. Get 10 active Flings, AI avatars, all Recap themes, photo sharing, and priority support.',
    keywords: ['FlingLink Plus', 'premium', 'upgrade', 'Plus features'],
    category: 'Plus & Billing',
    categorySlug: 'plus-billing',
    content: `
## FlingLink Plus

Plus is FlingLink's premium membership — more features, more Flings, more fun.

### What You Get with Plus

**10 Active Flings**
Free users get 1 active Fling. Plus members can run up to 10 simultaneously. For the serial daters (we don't judge).

**AI Avatar Generation**
Create unique AI-generated avatars. Fun, creative, and no selfie required.

**All Recap Themes**
Access every Recap design, including exclusive Plus-only themes.

**Photo Sharing in Chat**
Share photos directly in your Fling chats.

**Priority Support**
Jump the queue when you need help.

**Exclusive Features**
First access to new features as we release them.

### Plus Pricing

**Monthly:** £6.99/month
**Yearly:** £59.99/year (save 30%)

**Plus Experience:** £2 one-time for 7 days (try Plus features for one Fling)

### Is Plus Worth It?

**Get Plus if you:**
- Want multiple active Flings
- Love customization (themes, avatars)
- Want photo sharing in chat
- Value priority support

**Stick with Free if you:**
- Only need one Fling at a time
- Happy with basic features
- Just testing the waters

### How to Upgrade

1. Go to **Settings → Upgrade**
2. Choose Monthly or Yearly
3. Complete secure checkout
4. Plus activated immediately!
    `,
    relatedArticles: [
      'upgrade-plus',
      'manage-subscription',
      'in-app-purchases',
    ],
  },
  {
    id: 'upgrade-plus',
    slug: 'upgrade-plus',
    title: 'Upgrade to Plus',
    seoTitle: 'Upgrade to FlingLink Plus | Subscription Plans & Pricing',
    metaDescription:
      'Upgrade to FlingLink Plus. Choose monthly or yearly plans, complete secure checkout, and unlock premium features instantly.',
    keywords: [
      'upgrade Plus',
      'FlingLink subscription',
      'buy Plus',
      'Plus pricing',
    ],
    category: 'Plus & Billing',
    categorySlug: 'plus-billing',
    content: `
## How to Upgrade to Plus

Ready for the full FlingLink experience? Here's how to go Plus.

### Subscription Options

**Monthly Plan**
- £6.99/month
- Cancel anytime
- Billed monthly

**Yearly Plan**
- £59.99/year
- Save 30% vs monthly
- Billed annually

**Plus Experience (One-Time)**
- £2 for 7 days
- All Plus features
- No subscription required
- Perfect for trying it out

### Step-by-Step Upgrade

1. Open FlingLink
2. Go to **Settings → Upgrade**
3. Choose your plan (Monthly, Yearly, or Plus Experience)
4. Tap **"Get Plus"** or **"Try Plus Experience"**
5. Complete checkout via Stripe
6. Plus features unlock immediately!

### Payment Methods

We accept:
- Credit cards (Visa, Mastercard, Amex)
- Debit cards
- Apple Pay (where available)
- Google Pay (where available)

All payments processed securely through Stripe.

### After Upgrading

- Features unlock instantly
- Receipt emailed to you
- Manage subscription in Settings → Billing
- Cancel anytime (keep Plus until period ends)

### Questions?

Check out [Managing Subscriptions](/help/manage-subscription) or [contact support](/help/report).
    `,
    relatedArticles: [
      'flinglink-plus',
      'manage-subscription',
      'payment-issues',
    ],
  },
  {
    id: 'in-app-purchases',
    slug: 'in-app-purchases',
    title: 'In-App Purchases',
    seoTitle: 'FlingLink Purchases | Spark Decks, Themes & Add-ons',
    metaDescription:
      'Buy FlingLink add-ons without a subscription. Get Spark Decks, Recap themes, and the Plus Experience as one-time purchases.',
    keywords: [
      'in-app purchases',
      'Spark Decks',
      'Recap themes',
      'buy add-ons',
    ],
    category: 'Plus & Billing',
    categorySlug: 'plus-billing',
    content: `
## In-App Purchases

Want upgrades without a subscription? Buy individual add-ons.

### Available Purchases

**Spark Deck Packs — £2.99 each**
Premium question collections:
- **Deep Talk** — Meaningful conversations
- **Spicy** — Turn up the heat 🌶️
- **Future Planning** — Where is this going?
- **Nostalgia** — Childhood memories

**Recap Theme Packs — £1.99 each**
Exclusive card designs:
- Gradient vibes
- Dark mode aesthetic
- Seasonal specials
- Limited editions

**Plus Experience — £2 for 7 days**
All Plus features for one Fling cycle. Perfect for trying before subscribing.

### How to Purchase

1. Go to **Settings → Upgrade**
2. Browse available items
3. Tap the one you want
4. Complete checkout
5. Item unlocks immediately

### One-Time vs Subscription

**One-Time Purchases:**
- Pay once, own forever (Spark Decks, Themes)
- No recurring charges
- Use whenever you want

**Plus Experience:**
- £2 for 7 days of Plus features
- Great for special occasions
- No auto-renewal

**Plus Subscription:**
- Monthly or yearly billing
- All features included
- Best value for regular users
    `,
    relatedArticles: ['flinglink-plus', 'upgrade-plus', 'daily-sparks'],
  },
  {
    id: 'manage-subscription',
    slug: 'manage-subscription',
    title: 'Managing Subscriptions',
    seoTitle: 'Manage FlingLink Subscription | Cancel, Change & Billing',
    metaDescription:
      'Manage your FlingLink Plus subscription. Update payment methods, change plans, cancel anytime, and access billing history.',
    keywords: [
      'manage subscription',
      'cancel Plus',
      'billing',
      'payment method',
    ],
    category: 'Plus & Billing',
    categorySlug: 'plus-billing',
    content: `
## Managing Your Subscription

Full control over your Plus membership.

### Accessing Billing

1. Go to **Settings**
2. Tap **"Billing"** or **"Manage Subscription"**
3. Opens secure Stripe billing portal

### What You Can Do

**View Current Plan**
See your subscription type, price, and next billing date.

**Update Payment Method**
Change your card or add a new payment method.

**Change Billing Cycle**
Switch between monthly and yearly (changes apply at next renewal).

**View Billing History**
See all past payments and download receipts.

**Cancel Subscription**
End your Plus membership (keep access until period ends).

### Cancelling Plus

1. Go to Settings → Billing
2. Click "Cancel Subscription"
3. Confirm cancellation
4. Keep Plus features until your current period ends
5. Revert to free tier after expiry

**Note:** Your data is preserved. If you resubscribe later, everything's still there.

### Refunds

- Contact support for refund requests
- Reviewed case-by-case
- Pro-rata refunds may apply for annual plans
- Reach out at help@flinglink.app

### Payment Issues?

Check our [payment troubleshooting guide](/help/payment-issues).
    `,
    relatedArticles: ['flinglink-plus', 'upgrade-plus', 'payment-issues'],
  },

  // SAFETY & SUPPORT
  {
    id: 'block-users',
    slug: 'block-users',
    title: 'Blocking Users',
    seoTitle: 'How to Block Someone on FlingLink | Stop Unwanted Contact',
    metaDescription:
      'Block users on FlingLink to stop unwanted contact. Learn how to block, what happens when you block, and how to manage your block list.',
    keywords: [
      'block user',
      'FlingLink block',
      'stop contact',
      'unwanted messages',
    ],
    category: 'Safety & Support',
    categorySlug: 'safety-support',
    content: `
## Blocking Users

Your safety comes first. Here's how to block someone on FlingLink.

### When to Block

- Unwanted messages or contact
- Inappropriate behavior
- Harassment or abuse
- You simply don't want to connect
- Any situation where you feel uncomfortable

### How to Block

1. Go to **Settings → Privacy**
2. Tap **"Block User"**
3. Enter their username
4. Tap **"Block"** to confirm

### What Happens When You Block

**Immediately:**
- All communication stops
- They can't message you
- They can't start new Flings with you
- Existing Flings are affected

**They Won't Know:**
- Blocks are private
- They're NOT notified
- They might figure it out eventually, but we don't tell them

### Managing Your Block List

View and manage blocked users:
1. Go to **Settings → Privacy**
2. Tap **"Blocked Users"**
3. See your full block list
4. Unblock anyone if you change your mind

### Unblocking Someone

1. Go to your block list
2. Find the person
3. Tap **"Unblock"**
4. They can contact you again

### Block vs Report

**Block:** Stops contact from that person
**Report:** Flags them for our team to review

You can do both if needed. If someone's behavior is serious, please report them too.
    `,
    relatedArticles: ['report', 'privacy-settings'],
  },
  {
    id: 'report',
    slug: 'report',
    title: 'Reporting Issues',
    seoTitle: 'Report on FlingLink | Safety, Bugs & Inappropriate Content',
    metaDescription:
      'Report issues on FlingLink. Flag inappropriate behavior, content violations, bugs, or safety concerns for our team to review.',
    keywords: [
      'report FlingLink',
      'report user',
      'flag content',
      'safety report',
    ],
    category: 'Safety & Support',
    categorySlug: 'safety-support',
    content: `
## Reporting Issues

See something wrong? Let us know. We take reports seriously.

### What to Report

**Behavior Issues:**
- Harassment or abuse
- Threats or intimidation
- Stalking behavior
- Impersonation

**Content Issues:**
- Inappropriate messages or media
- Spam or scams
- Illegal content
- Terms of Service violations

**Technical Issues:**
- Bugs or errors
- App not working properly
- Features broken
- Performance problems

### How to Report

1. Go to **Settings → Privacy**
2. Tap **"Report"**
3. Select the category
4. Provide details (the more info, the better)
5. Submit

### What Happens Next

1. **We receive your report** — Usually within minutes
2. **Review begins** — Our team investigates within 24-48 hours
3. **Action taken** — Warnings, suspensions, or bans as appropriate
4. **You may be contacted** — If we need more info

### Reporter Privacy

- Your identity is kept confidential
- The reported person doesn't know who reported them
- We may contact you for follow-up

### Urgent Safety Concerns

If you're in immediate danger, contact local emergency services first. FlingLink reports are not monitored 24/7.

### Contact Support Directly

For non-urgent issues or questions:
**Email:** help@flinglink.app
    `,
    relatedArticles: ['block-users', 'privacy-settings'],
  },
  {
    id: 'delete-account',
    slug: 'delete-account',
    title: 'Account Deletion',
    seoTitle: 'Delete FlingLink Account | Permanent Removal & Data',
    metaDescription:
      'Delete your FlingLink account permanently. Learn what gets deleted, what stays, and how to download your data first.',
    keywords: [
      'delete account',
      'remove FlingLink',
      'account deletion',
      'delete data',
    ],
    category: 'Safety & Support',
    categorySlug: 'safety-support',
    content: `
## Deleting Your Account

Want to leave FlingLink? Here's how to delete your account permanently.

### Before You Delete

**Cancel Plus first** (if subscribed)
Go to Settings → Billing → Cancel Subscription

**Download your Recaps**
Save any Recap cards you want to keep — they'll be gone forever.

**Know it's permanent**
Account deletion cannot be undone. All your data will be removed.

### How to Delete Your Account

1. Go to **Settings → Profile**
2. Scroll to the bottom
3. Tap **"Delete Account"**
4. Read the warning carefully
5. Enter your password to confirm
6. Tap **"Delete Permanently"**

### What Gets Deleted

- Your profile and account info
- All Flings (active and past)
- All chat messages
- Photos and avatars
- Fling history and Recaps
- Notification preferences

### What Stays

- **Anonymized analytics** — Aggregate data with no personal info
- **Legal records** — Required by law for a limited time
- **Payment records** — Stored by Stripe for their requirements

### After Deletion

- You'll be logged out immediately
- Your username becomes available again
- You can create a new account with the same email later
- Previous data will NOT be restored

### Changed Your Mind?

If you just need a break, consider:
- Turning off notifications instead
- Blocking specific users
- Just... not opening the app for a while

Deletion is forever. Make sure it's what you want.
    `,
    relatedArticles: ['privacy-settings', 'manage-subscription'],
  },

  // TROUBLESHOOTING
  {
    id: 'login-issues',
    slug: 'login-issues',
    title: 'Login Issues',
    seoTitle: "Can't Log In to FlingLink? | Fix Login Problems Fast",
    metaDescription:
      "Can't log in to FlingLink? Fix password issues, magic link problems, and social login errors with our troubleshooting guide.",
    keywords: [
      'login issues',
      "can't log in",
      'FlingLink login problem',
      'password reset',
    ],
    category: 'Troubleshooting',
    categorySlug: 'troubleshooting',
    content: `
## Login Issues

Can't get into your account? Let's fix that.

### Forgot Password

1. Tap **"Forgot password?"** on the login screen
2. Enter your email address
3. Check your inbox for a reset link
4. Click the link (expires in 1 hour)
5. Create a new password
6. Log in with your fresh password

**Not getting the email?**
- Check spam/junk folder
- Make sure you entered the right email
- Wait a few minutes and try again
- Try a different email client

### Magic Link Not Working

**Link expired?**
Magic links expire after 15 minutes. Request a new one.

**Not in inbox?**
- Check spam/junk folder
- Add noreply@flinglink.app to contacts
- Try a different email client

**Link not opening?**
- Copy and paste the full URL
- Try a different browser
- Make sure you're clicking the right link

### Social Login Failing

**Google/TikTok/Discord/etc not working?**

1. Check your internet connection
2. Make sure you've granted OAuth permissions
3. Try logging out of the social platform first
4. Clear browser cookies and try again
5. Try a different browser
6. Try the email/password method instead

### Account Locked?

If you've tried too many times:
- Wait 15 minutes before trying again
- Use the password reset option
- Contact support if it persists

### Still Stuck?

Email us at **help@flinglink.app** with:
- Your email address
- What you've tried
- Any error messages you see
    `,
    relatedArticles: ['login', 'signup', 'notification-issues'],
  },
  {
    id: 'notification-issues',
    slug: 'notification-issues',
    title: 'Notification Problems',
    seoTitle: 'FlingLink Notifications Not Working? | Fix Push & Email',
    metaDescription:
      'FlingLink notifications not working? Fix push notification and email alert issues on iOS, Android, and desktop browsers.',
    keywords: [
      'notifications not working',
      'push notifications',
      'no alerts',
      'FlingLink notifications',
    ],
    category: 'Troubleshooting',
    categorySlug: 'troubleshooting',
    content: `
## Notification Problems

Not getting notifications? Here's how to fix it.

### Push Notifications Not Appearing

**On iPhone (iOS):**

1. Go to **Settings → Safari → Notifications**
2. Make sure notifications are **allowed**
3. Check FlingLink has permission
4. Make sure you're not in **Focus/Do Not Disturb** mode
5. Try reinstalling the PWA

**On Android:**

1. Go to **Settings → Apps → Chrome**
2. Tap **Notifications**
3. Make sure they're **enabled**
4. Check battery optimization isn't blocking notifications
5. Try reinstalling the PWA

**On Desktop:**

1. Click the **lock icon** in the address bar
2. Find **Notifications**
3. Set to **Allow**
4. Refresh the page

### Email Notifications Not Arriving

1. Check your **spam/junk folder**
2. Add **noreply@flinglink.app** to your contacts
3. Verify your email address is correct in Settings
4. Check your email provider isn't blocking us

### In-App Notification Settings

Make sure notifications are enabled in FlingLink:

1. Go to **Settings → Notifications**
2. Toggle on the notifications you want
3. Save changes

### Still Not Working?

Try these:
- Log out and back in
- Reinstall the PWA
- Try a different browser
- Clear browser cache
- Contact support with your device/browser info
    `,
    relatedArticles: ['notification-settings', 'install', 'login-issues'],
  },
  {
    id: 'invite-issues',
    slug: 'invite-issues',
    title: 'Invite Link Issues',
    seoTitle: 'FlingLink Invite Link Not Working? | Fix Sharing Problems',
    metaDescription:
      'FlingLink invite link not working? Fix expired links, PIN issues, and sharing problems with our troubleshooting guide.',
    keywords: [
      'invite link',
      'link not working',
      'Fling invite',
      'PIN not working',
    ],
    category: 'Troubleshooting',
    categorySlug: 'troubleshooting',
    content: `
## Invite Link Issues

Having trouble with Fling invites? Let's sort it out.

### Link Expired

Invite links expire after **7 days**. If the link isn't working:

1. Ask the sender to create a **new invite**
2. They can do this from their Flings tab
3. Get the fresh link and try again

### Link Not Opening

**On mobile:**
- Make sure you have internet connection
- Try copying the link and pasting in browser
- Use a different messaging app to open it
- Check for typos if manually entered

**On desktop:**
- Try a different browser
- Clear cookies and cache
- Make sure JavaScript is enabled

### PIN Not Working

If the invite has PIN protection:

1. Double-check you have the **correct 4-digit PIN**
2. Watch for extra spaces when copying
3. Ask the sender to **confirm the PIN**
4. They can send a **new invite** with a different PIN if needed

### Can't Find the Link

- Check your messages/DMs where they sent it
- Search for "flingl.ink" in your messages
- Ask them to resend it
- Make sure they actually sent it (sometimes people forget!)

### Link Opens But Can't Join

- Make sure you're **logged in** to FlingLink
- Create an account if you don't have one
- Check if the invite was **cancelled** by the sender
- The sender might have reached their **Fling limit** (Free: 1, Plus: 10)
    `,
    relatedArticles: ['create-fling', 'accept-fling', 'login-issues'],
  },
  {
    id: 'payment-issues',
    slug: 'payment-issues',
    title: 'Payment & Billing Issues',
    seoTitle: 'FlingLink Payment Failed? | Fix Billing & Subscription',
    metaDescription:
      'FlingLink payment failed? Fix card declined errors, subscription issues, and billing problems with our troubleshooting guide.',
    keywords: [
      'payment failed',
      'card declined',
      'billing issues',
      'subscription problem',
    ],
    category: 'Troubleshooting',
    categorySlug: 'troubleshooting',
    content: `
## Payment & Billing Issues

Payment not going through? Here's how to fix it.

### Card Declined

**Common reasons:**
- Insufficient funds
- Card expired
- Bank blocked the transaction
- Incorrect card details

**Try these:**
1. Check your card has **sufficient funds**
2. Verify the card **hasn't expired**
3. Make sure details are entered **correctly**
4. Try a **different card**
5. Contact your bank — they might have blocked it

### Subscription Not Activating

Paid but Plus isn't working?

1. Check your email for a **confirmation receipt**
2. **Log out and back in** to refresh your account
3. Wait a few minutes — sometimes it takes a moment
4. Check **Settings → Billing** to verify status
5. Contact support with your receipt if it's still not working

### Can't Access Billing Portal

1. Go to **Settings → Billing**
2. Tap **"Manage Subscription"**
3. Should open Stripe's secure portal
4. If it doesn't load, try a different browser
5. Clear cookies and try again

### Charged But No Plus

If you were charged but don't have Plus:
1. Check your email for the receipt
2. Note the transaction ID
3. Email **help@flinglink.app** with:
   - Your FlingLink email
   - Transaction ID or receipt
   - Date of purchase
   - Amount charged

We'll sort it out ASAP.

### Refund Requests

- Email **help@flinglink.app**
- Include your account email and reason
- Refunds reviewed case-by-case
- Pro-rata refunds may apply for annual plans
    `,
    relatedArticles: ['manage-subscription', 'upgrade-plus', 'flinglink-plus'],
  },
  {
    id: 'performance-issues',
    slug: 'performance-issues',
    title: 'App Performance',
    seoTitle: 'FlingLink Slow or Crashing? | Performance Fix Guide',
    metaDescription:
      'FlingLink running slow or crashing? Fix performance issues, loading problems, and app errors with our troubleshooting guide.',
    keywords: [
      'FlingLink slow',
      'app crashing',
      'performance issues',
      'not loading',
    ],
    category: 'Troubleshooting',
    categorySlug: 'troubleshooting',
    content: `
## App Performance Issues

FlingLink running slow or acting weird? Let's speed things up.

### App Loading Slowly

1. **Check your internet** — Slow connection = slow app
2. **Close other tabs** — Free up browser resources
3. **Clear browser cache** — Old data can slow things down
4. **Refresh the page** — Sometimes that's all it takes
5. **Try a different browser** — Chrome usually works best

### App Crashing

1. **Update your browser** — Old versions can cause crashes
2. **Reinstall the PWA** — Remove and re-add to home screen
3. **Try a different browser** — See if the issue persists
4. **Clear all site data** — Nuclear option but often works
5. **Report the bug** — Help us fix it for everyone

### Features Not Working

1. **Make sure JavaScript is enabled** — Required for FlingLink
2. **Disable browser extensions** — Some can interfere
3. **Try incognito/private mode** — Rules out extension issues
4. **Check for browser updates** — Keep it current
5. **Try a different device** — Isolate the problem

### Specific Issues

**Chat not loading?**
- Check internet connection
- Refresh the page
- Log out and back in

**Sparks not appearing?**
- Make sure it's a new day (resets at midnight)
- Refresh the page
- Check if Fling is still active

**Images not uploading?**
- Check file size (keep it reasonable)
- Try a different image format
- Make sure you have Plus (for chat photos)

### Still Having Issues?

Report the bug with:
- What you were doing when it happened
- Any error messages
- Your device and browser info
- Screenshots if possible

Email: **help@flinglink.app**
    `,
    relatedArticles: ['install', 'login-issues', 'report'],
  },
  {
    id: 'faq',
    slug: 'faq',
    title: 'Frequently Asked Questions',
    seoTitle: 'FlingLink FAQ | Common Questions & Quick Answers',
    metaDescription:
      'Get quick answers to common FlingLink questions. Learn about Flings, Plus membership, privacy, and more in our FAQ.',
    keywords: [
      'FlingLink FAQ',
      'frequently asked questions',
      'FlingLink help',
      'common questions',
    ],
    category: 'Troubleshooting',
    categorySlug: 'troubleshooting',
    content: `
## Frequently Asked Questions

Quick answers to the questions we get asked most.

### General

**Is FlingLink free?**
Yes! The core experience is free. Plus membership adds extra features for £6.99/month.

**Can I use FlingLink on desktop?**
Absolutely. Works in any modern browser — Chrome, Safari, Firefox, Edge.

**Is my data secure?**
Yes. Chats are end-to-end encrypted. We don't sell your data. See our [Privacy Policy](/privacy).

**Is FlingLink a dating app?**
Not exactly. It's a connection tool for people you already know or have seen on socials. No swiping, no feed.

### Flings

**How long is a Fling?**
7 days, from when both people accept.

**Can I extend a Fling?**
No, but you can start a new one after it ends.

**What happens after Day 7?**
You both decide: share numbers, friendly landing, or let it expire. Either way, you get a Recap.

**Can I have multiple Flings?**
Free: 1 active Fling. Plus: up to 10 simultaneously.

**Where do we chat?**
In FlingLink, or keep it on your socials (Insta, WhatsApp, etc.) — your choice!

### Plus Membership

**What's included in Plus?**
10 active Flings, AI avatars, all Recap themes, photo sharing in chat, priority support.

**Can I cancel anytime?**
Yes, via the billing portal. Keep Plus until your period ends.

**Do I lose data if I cancel?**
No. You just revert to free limits. Data stays.

**What's the Plus Experience?**
£2 one-time purchase for 7 days of Plus features. Great for trying it out.

### Privacy & Safety

**Is chat encrypted?**
Yes, end-to-end encrypted. Only you and your partner can read messages.

**Can someone see my Flings?**
Only your Fling partner can see your shared chat. No one else.

**How do I block someone?**
Settings → Privacy → Block User. They won't be notified.

**How do I delete my account?**
Settings → Profile → Delete Account. It's permanent.

### Still Have Questions?

Can't find what you're looking for? Email us at **help@flinglink.app** — we're here to help!
    `,
    relatedArticles: ['what-is-fling', 'flinglink-plus', 'privacy-settings'],
  },
]

export function getArticleBySlug(slug: string): HelpArticle | undefined {
  return helpArticles.find((article) => article.slug === slug)
}

export function getArticlesByCategory(categorySlug: string): HelpArticle[] {
  return helpArticles.filter((article) => article.categorySlug === categorySlug)
}

export function searchArticles(query: string): HelpArticle[] {
  const lowerQuery = query.toLowerCase()
  return helpArticles.filter(
    (article) =>
      article.title.toLowerCase().includes(lowerQuery) ||
      article.content.toLowerCase().includes(lowerQuery) ||
      article.keywords.some((kw) => kw.toLowerCase().includes(lowerQuery)),
  )
}
